import {
    StyledDecisionSupport,
    StyledEdit,
} from '@/components/decisionSupport/DecisionSupport.styled';
import { LegacyDecisionSupportRecommendations } from '@/components/decisionSupport/LegacyDecisionSupportRecommendations';
import { DateRecommendationFragment } from '@/components/decisionSupport/queries/dateRecommendations.gql.types';
import { RecommendationCell } from '@/components/decisionSupport/tables/RecommendationCell';
import {
    buildRecommendedColumn,
    getRange,
    useAxleRecommended,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { ManagedAxleDialogBase } from '@/dialogs/ManagedAxleDialog';
import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { Button, IconButton } from '@mui/material';
import { type ColumnDef } from '@tanstack/react-table';
import { ComponentType, FC, useCallback, useMemo, useState } from 'react';

const getValueFromRecommendation = (item: DateRecommendationFragment | null) =>
    GraphInputHelpers.date(item?.date ?? null);

type DateDecisionSupportContentProps = {
    onClose(): void;
    selectedDateRecommendation: DateRecommendationFragment | null;
    setSelectedDate(date: DateRecommendationFragment | null): void;
    recommendations: DateRecommendationFragment[];
};

const DateDecisionSupportContent: FC<DateDecisionSupportContentProps> = (props) => {
    const { onClose, recommendations, selectedDateRecommendation, setSelectedDate } = props;
    const [isViewingMore, setIsViewingMore] = useState(false);

    const axleRecommendedDate = useAxleRecommended(recommendations);

    const data = useMemo(
        () =>
            recommendations
                .slice()
                .sort((a, b) => a.date.getTime() - b.date.getTime())
                .slice(0, isViewingMore ? undefined : 7),
        [isViewingMore, recommendations],
    );

    const distanceScoreRange = useMemo(() => getRange(data, 'distanceScore'), [data]);
    const numIncompleteVisitsOnDateForBusiestClinicianRange = useMemo(
        () => getRange(data, 'numIncompleteVisitsOnDateForBusiestClinician'),
        [data],
    );

    const columns: ColumnDef<DateRecommendationFragment>[] = useMemo(
        () => [
            buildRecommendedColumn<DateRecommendationFragment>({
                id: 'date',
                header: 'Date',
                getLabel: (row) => row.date.toDateString(),
                isRecommended: (row) =>
                    GraphInputHelpers.date(row.date) ===
                    GraphInputHelpers.date(axleRecommendedDate?.date ?? null),
            }),
            {
                id: 'hasShifts',
                header: 'Shift Availability',
                cell: ({ row }) => <RecommendationCell value={row.original.hasShifts} />,
            },
            {
                id: 'distanceScore',
                header: 'Proximity to Visits',
                cell: ({ row }) => (
                    <RecommendationCell
                        value={row.original.distanceScore}
                        range={distanceScoreRange}
                        roundedTo={25}
                        isInverse
                    />
                ),
            },
            {
                id: 'numIncompleteVisitsOnDateForBusiestClinician',
                header: 'Daily Volume',
                cell: ({ row }) => (
                    <RecommendationCell
                        value={row.original.numIncompleteVisitsOnDateForBusiestClinician}
                        range={numIncompleteVisitsOnDateForBusiestClinicianRange}
                        roundedTo={1}
                        isInverse
                    />
                ),
            },
        ],
        [
            axleRecommendedDate?.date,
            distanceScoreRange,
            numIncompleteVisitsOnDateForBusiestClinicianRange,
        ],
    );

    const onSubmit = useCallback(
        (selection: DateRecommendationFragment | null) => {
            setSelectedDate(selection);
            onClose();
        },
        [onClose, setSelectedDate],
    );

    const onClickShowMore = useCallback(() => {
        setIsViewingMore((prev) => !prev);
    }, []);

    const Actions: ComponentType = useCallback(
        () => (
            <>
                {recommendations.length > 7 ? (
                    <Button color='primary' onClick={onClickShowMore}>
                        View {data.length < recommendations.length ? 'More' : 'Less'}
                    </Button>
                ) : null}
            </>
        ),
        [data.length, onClickShowMore, recommendations.length],
    );

    return (
        <LegacyDecisionSupportRecommendations
            Actions={Actions}
            recommendations={data}
            selection={selectedDateRecommendation}
            getValueFromRecommendation={getValueFromRecommendation}
            columns={columns}
            onSubmit={onSubmit}
        />
    );
};

type DateDecisionSupportDialogProps = {
    onClose(): void;
    selectedDateRecommendation: DateRecommendationFragment | null;
    setSelectedDate(date: DateRecommendationFragment | null): void;
    recommendations: DateRecommendationFragment[];
};

export const DateDecisionSupportDialog: FC<DateDecisionSupportDialogProps> = (props) => {
    const { onClose, ...rest } = props;
    return (
        <ManagedAxleDialogBase title='Date Recommendations' onClose={onClose}>
            {({ onClose }) => <DateDecisionSupportContent onClose={onClose} {...rest} />}
        </ManagedAxleDialogBase>
    );
};

type DateDecisionSupportProps = {
    selectedDateRecommendation: DateRecommendationFragment | null;
    setSelectedDate(date: DateRecommendationFragment | null): void;
    recommendations: DateRecommendationFragment[];
    disabled?: boolean;
};

export const DateDecisionSupport: FC<DateDecisionSupportProps> = (props) => {
    const { selectedDateRecommendation, setSelectedDate, recommendations, disabled } = props;
    const { openDialog } = useDialog();

    const onClickEdit = useCallback(() => {
        openDialog(DialogType.DateDecisionSupport, {
            setSelectedDate,
            selectedDateRecommendation,
            recommendations,
        });
    }, [openDialog, recommendations, selectedDateRecommendation, setSelectedDate]);

    if (recommendations.length === 0) {
        return null;
    }

    return (
        <StyledDecisionSupport>
            <IconButton onClick={onClickEdit} disabled={disabled}>
                <StyledEdit />
            </IconButton>
        </StyledDecisionSupport>
    );
};
